import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/clients/supabase';
import { FastLoadingScreen } from '../../../shared/components/FastLoadingScreen';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'guest' | 'member' | 'admin' | 'superadmin';
  redirectTo?: string;
}



/**
 * ProtectedRoute Component
 * 
 * Wraps routes that require authentication and/or specific roles.
 * 
 * Features:
 * - Redirects unauthenticated users to login
 * - Preserves intended destination for post-login redirect
 * - Supports role-based access control with fresh database queries
 * - Shows loading state during auth and role verification
 * - Queries fresh role from database on every route access
 * 
 * @param children - The protected content to render
 * @param requiredRole - Optional role requirement ('member' or 'admin')
 * @param redirectTo - Optional custom redirect path (defaults to '/login')
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  redirectTo = '/login'
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const [roleCheck, setRoleCheck] = useState<'checking' | 'allowed' | 'denied'>('checking');
  const [freshRole, setFreshRole] = useState<string | null>(null);

  // Query fresh role from database when user or requiredRole changes
  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    const checkRole = async () => {
      // Reset to checking state
      setRoleCheck('checking');
      
      // Set timeout to prevent infinite loading (5 seconds max)
      timeoutId = setTimeout(() => {
        if (isMounted) {
          console.warn('⚠️ Role check timeout - allowing access with fallback');
          // On timeout, allow access if no role requirement
          if (!requiredRole) {
            setRoleCheck('allowed');
          } else if (user?.role === 'superadmin') {
            // Superadmin always has access
            setRoleCheck('allowed');
          } else if (user?.role === requiredRole) {
            // Use cached role as fallback
            setRoleCheck('allowed');
          } else {
            setRoleCheck('denied');
          }
        }
      }, 5000);
      
      // If no user or still loading auth, wait but don't get stuck
      if (!user || isLoading) {
        // If auth is loading, wait for it to complete
        if (isLoading) {
          return;
        }
        
        // If no user and not loading, deny access
        if (!user) {
          clearTimeout(timeoutId);
          setRoleCheck('denied');
          return;
        }
      }

      // If no role requirement, allow access immediately
      if (!requiredRole) {
        clearTimeout(timeoutId);
        setRoleCheck('allowed');
        return;
      }

      try {
        // Query fresh role from database with timeout
        const roleQueryPromise = supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();

        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Role query timeout')), 3000)
        );

        const { data, error } = await Promise.race([
          roleQueryPromise,
          timeoutPromise
        ]) as any;

        if (!isMounted) return;
        clearTimeout(timeoutId);

        if (error) {
          console.error('Failed to verify role:', error);
          // On error, fall back to user state role
          const currentRole = user.role;
          setFreshRole(currentRole);
          
          // Superadmin has access to everything
          if (currentRole === 'superadmin') {
            setRoleCheck('allowed');
          } else if (currentRole === requiredRole) {
            setRoleCheck('allowed');
          } else {
            setRoleCheck('denied');
          }
          return;
        }

        const currentRole = data?.role;
        setFreshRole(currentRole);

        // Check if role matches requirement
        // Superadmin has access to everything (including admin routes)
        if (currentRole === 'superadmin') {
          setRoleCheck('allowed');
        } else if (currentRole === requiredRole) {
          setRoleCheck('allowed');
        } else {
          setRoleCheck('denied');
        }
      } catch (error) {
        if (!isMounted) return;
        clearTimeout(timeoutId);
        
        console.error('Role verification exception:', error);
        // On exception, fall back to user state role
        const currentRole = user.role;
        setFreshRole(currentRole);
        
        // Superadmin has access to everything
        if (currentRole === 'superadmin') {
          setRoleCheck('allowed');
        } else if (currentRole === requiredRole) {
          setRoleCheck('allowed');
        } else {
          setRoleCheck('denied');
        }
      }
    };

    checkRole();

    // Cleanup function
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [user, requiredRole, isLoading]);

  // CRITICAL FIX: Add timeout for loading screen to prevent infinite loading
  // This prevents users from getting stuck on loading screen due to auth race conditions
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);
  
  React.useEffect(() => {
    if (isLoading || roleCheck === 'checking') {
      // Set timeout to force loading to complete after 8 seconds
      const timer = setTimeout(() => {
        console.warn('⚠️ Loading timeout reached - forcing auth resolution');
        setLoadingTimeout(true);
      }, 8000);
      
      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [isLoading, roleCheck]);

  // Show loading screen while checking authentication or role (with timeout)
  if ((isLoading || roleCheck === 'checking') && !loadingTimeout) {
    return (
      <FastLoadingScreen 
        message="Memverifikasi akses..."
        showProgress={true}
        maxDuration={5000}
        onTimeout={() => {
          console.warn('⚠️ ProtectedRoute loading timeout reached');
        }}
      />
    );
  }

  // Redirect to login if not authenticated
  // Preserve the current location to redirect back after login
  if (!isAuthenticated) {
    return (
      <Navigate 
        to={redirectTo} 
        replace 
        state={{ from: location.pathname }}
      />
    );
  }

  // Check email confirmation for authenticated users (except for email verification pages)
  const emailVerificationPaths = ['/check-email', '/email-confirmed', '/auth/confirm'];
  const isEmailVerificationPage = emailVerificationPaths.includes(location.pathname);
  
  if (isAuthenticated && user && !isEmailVerificationPage) {
    // Check if user's email is confirmed in their profile
    // Use user.email_confirmed_at from Supabase auth as the source of truth
    const isEmailConfirmed = user.email_confirmed_at || user.emailVerified;
    
    // Also check URL parameters for immediate confirmation (from email links)
    const urlSearchParams = new URLSearchParams(location.search);
    const urlHashParams = new URLSearchParams(location.hash.substring(1));
    const confirmationType = urlSearchParams.get('type') || urlHashParams.get('type');
    const hasConfirmationUrl = confirmationType === 'signup' || confirmationType === 'recovery';
    
    // SAFEGUARD: If user has valid token and can access dashboard, assume email is confirmed
    // This prevents redirect loops for users who are already authenticated
    const hasValidToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const isDashboardAccess = location.pathname.includes('/dashboard') || location.pathname === '/';
    const emailAlreadyProcessed = sessionStorage.getItem('email_confirmation_processed');
    
    // Only redirect to check-email if:
    // 1. Email is NOT confirmed in user profile AND
    // 2. User is NOT coming from email confirmation link AND
    // 3. Not already on email verification pages AND
    // 4. NOT accessing dashboard with valid token (safeguard) AND
    // 5. Email confirmation has NOT been processed before
    if (!isEmailConfirmed && !hasConfirmationUrl && !(hasValidToken && isDashboardAccess) && !emailAlreadyProcessed) {
      console.log('🔒 Email not confirmed, redirecting to check-email from:', location.pathname);
      console.log('📧 Email confirmed at:', user.email_confirmed_at);
      console.log('✅ Email verified flag:', user.emailVerified);
      console.log('🔗 Confirmation URL type:', confirmationType);
      console.log('🎫 Has valid token:', hasValidToken);
      console.log('🏠 Dashboard access:', isDashboardAccess);
      console.log('✅ Email processed:', emailAlreadyProcessed);
      
      return (
        <Navigate 
          to="/check-email" 
          replace 
          state={{ 
            email: user.email,
            from: location.pathname 
          }}
        />
      );
    }
  }

  // Check role-based access control
  if (roleCheck === 'denied') {
    // User is authenticated but doesn't have required role
    return (
      <Navigate 
        to="/unauthorized" 
        replace 
        state={{ 
          requiredRole,
          userRole: freshRole || user?.role,
          from: location.pathname 
        }}
      />
    );
  }

  // User is authenticated and has required role (if specified)
  return <>{children}</>;
};

/**
 * PublicRoute Component
 * 
 * Wraps routes that should only be accessible to unauthenticated users
 * (e.g., login, register pages)
 * 
 * @param children - The public content to render
 * @param redirectTo - Where to redirect authenticated users (defaults to '/dashboard')
 */
export const PublicRoute: React.FC<{
  children: React.ReactNode;
  redirectTo?: string;
}> = ({ children, redirectTo = '/dashboard' }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Redirect authenticated users away from public routes
  if (isAuthenticated && user) {
    // Check if user came from email confirmation
    const urlSearchParams = new URLSearchParams(location.search);
    const urlHashParams = new URLSearchParams(location.hash.substring(1));
    const confirmationType = urlSearchParams.get('type') || urlHashParams.get('type');
    
    // If no confirmation, redirect to check-email instead of dashboard
    if (!confirmationType || (confirmationType !== 'signup' && confirmationType !== 'recovery')) {
      return <Navigate to="/check-email" replace state={{ email: user.email }} />;
    }
    
    // If confirmed, redirect to intended destination
    const from = (location.state as any)?.from || redirectTo;
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
};

/**
 * RoleGuard Component
 * 
 * Can be used within protected routes to conditionally render content
 * based on user role without redirecting
 * 
 * @param children - Content to render if role matches
 * @param allowedRoles - Array of roles that can see the content
 * @param fallback - Optional fallback content for unauthorized users
 */
export const RoleGuard: React.FC<{
  children: React.ReactNode;
  allowedRoles: Array<'guest' | 'member' | 'admin' | 'superadmin'>;
  fallback?: React.ReactNode;
}> = ({ children, allowedRoles, fallback = null }) => {
  const { user } = useAuth();

  // Superadmin has access to everything
  if (user?.role === 'superadmin') {
    return <>{children}</>;
  }

  if (!user || !allowedRoles.includes(user.role)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
